import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
  Autocomplete
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import AppLayout from '../../components/layout/AppLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FormErrorSummary from '../../components/common/FormErrorSummary';
import usersService, { type CreateUserDto, type UpdateUserDto } from '../../services/usersService';
import tipologieService from '../../services/tipologieService';
import { Role } from '../../types/user.types';
import type { TipologiaScadenza } from '../../types/tipologia.types';
import { getErrorMessage } from '../../utils/api';

interface UserFormData {
  email: string;
  password: string;
  nome: string;
  cognome: string;
  ruolo: Role;
  tipologieIds: string[];
}

const UserFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!id;

  const [selectedRole, setSelectedRole] = useState<Role>(Role.OPERATORE);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<UserFormData>({
    defaultValues: {
      email: '',
      password: '',
      nome: '',
      cognome: '',
      ruolo: Role.OPERATORE,
      tipologieIds: []
    }
  });

  const ruoloValue = watch('ruolo');

  // Fetch user if editing
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersService.getById(id!),
    enabled: isEdit
  });

  // Fetch tipologie (for operatore role)
  const { data: tipologie } = useQuery({
    queryKey: ['tipologie-active'],
    queryFn: () => tipologieService.getAllActive()
  });

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (params: { userData: CreateUserDto | UpdateUserDto; tipologieIds?: string[] }) => {
      if (isEdit) {
        const updateData: UpdateUserDto = {
          email: params.userData.email,
          nome: params.userData.nome,
          cognome: params.userData.cognome,
          ruolo: params.userData.ruolo
        };
        const updatedUser = await usersService.update(id!, updateData);

        // Assign tipologie if provided
        if (params.tipologieIds !== undefined) {
          await usersService.assignTipologie(id!, { tipologieIds: params.tipologieIds });
        }

        return updatedUser;
      } else {
        // Create user first
        const createdUser = await usersService.create(params.userData as CreateUserDto);

        // Get user ID (handle both 'id' and '_id' from backend)
        const userId = createdUser.id || (createdUser as any)._id;

        // Then assign tipologie if provided
        if (params.tipologieIds && params.tipologieIds.length > 0 && userId) {
          await usersService.assignTipologie(userId, { tipologieIds: params.tipologieIds });
        }

        return createdUser;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      enqueueSnackbar(
        isEdit ? 'Utente aggiornato con successo' : 'Utente creato con successo',
        { variant: 'success' }
      );
      navigate('/users');
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante il salvataggio'), { variant: 'error' });
    }
  });

  useEffect(() => {
    if (user) {
      // Extract tipologie IDs from user data (handle both id and _id for compatibility)
      const tipologieIds = user.tipologieScadenzeAssegnate?.map(t => (t as any).id || t._id) || [];

      reset({
        email: user.email,
        password: '', // Leave empty for edit mode
        nome: user.nome,
        cognome: user.cognome,
        ruolo: user.ruolo,
        tipologieIds
      });
      setSelectedRole(user.ruolo);
    }
  }, [user, reset]);

  useEffect(() => {
    setSelectedRole(ruoloValue);
  }, [ruoloValue]);

  const onSubmit = (data: UserFormData) => {
    const userData: CreateUserDto | UpdateUserDto = {
      email: data.email,
      nome: data.nome,
      cognome: data.cognome,
      ruolo: data.ruolo
    };

    // Add password for new users or if provided in edit
    if (!isEdit) {
      (userData as CreateUserDto).password = data.password;
    }

    // Prepare tipologie IDs (only for OPERATORE role and only if not empty)
    const tipologieIds = data.ruolo === Role.OPERATORE && data.tipologieIds && data.tipologieIds.length > 0
      ? data.tipologieIds
      : undefined;

    saveMutation.mutate({ userData, tipologieIds });
  };

  if (isEdit && userLoading) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento utente..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/users')}
            variant="outlined"
          >
            Indietro
          </Button>
          <Typography variant="h4" component="h1" fontWeight={600}>
            {isEdit ? `Modifica Utente ${user?.nome || ''}` : 'Nuovo Utente'}
          </Typography>
        </Box>

        <Paper sx={{ p: 3 }}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            aria-label={isEdit ? 'Modifica utente' : 'Crea nuovo utente'}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormErrorSummary errors={errors} />

              {/* Row 1: Nome, Cognome */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Controller
                  name="nome"
                  control={control}
                  rules={{ required: 'Nome obbligatorio' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Nome"
                      required
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="cognome"
                  control={control}
                  rules={{ required: 'Cognome obbligatorio' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Cognome"
                      required
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Box>

              {/* Row 2: Email */}
              <Box>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Email obbligatoria',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Email non valida'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Email"
                      type="email"
                      required
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Box>

              {/* Row 3: Password, Ruolo */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: isEdit ? false : 'Password obbligatoria',
                    minLength: {
                      value: 8,
                      message: 'La password deve essere di almeno 8 caratteri'
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                      message: 'Password deve contenere: 1 maiuscola, 1 minuscola, 1 numero e 1 carattere speciale (@$!%*?&)'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Password"
                      type="password"
                      required={!isEdit}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        (isEdit ? 'Lascia vuoto per non modificare' : 'Min 8 caratteri: 1 maiuscola, 1 minuscola, 1 numero, 1 speciale (@$!%*?&)')
                      }
                    />
                  )}
                />
                <Controller
                  name="ruolo"
                  control={control}
                  rules={{ required: 'Ruolo obbligatorio' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      label="Ruolo"
                      required
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    >
                      <MenuItem value={Role.ADMIN}>Admin</MenuItem>
                      <MenuItem value={Role.OPERATORE}>Operatore</MenuItem>
                    </TextField>
                  )}
                />
              </Box>

              {/* Row 4: Tipologie Assegnate (only for OPERATORE) */}
              {selectedRole === Role.OPERATORE && (
                <Controller
                  name="tipologieIds"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (selectedRole === Role.OPERATORE) {
                        return (value && value.length > 0) || 'Seleziona almeno una tipologia per gli operatori';
                      }
                      return true;
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      multiple
                      options={tipologie || []}
                      getOptionLabel={(option: TipologiaScadenza) => option.nome}
                      isOptionEqualToValue={(option, value) => {
                        // Handle both _id (from backend) and id (if normalized)
                        const optionId = (option as any)._id || option.id;
                        const valueId = (value as any)._id || value.id;
                        return optionId === valueId;
                      }}
                      value={
                        tipologie?.filter((t) => {
                          const tipId = (t as any)._id || t.id;
                          return field.value?.includes(tipId);
                        }) || []
                      }
                      onChange={(_, newValue) => {
                        // Extract IDs, handling both _id and id
                        const ids = newValue.map((v) => (v as any)._id || v.id);
                        field.onChange(ids);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Tipologie Assegnate *"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message || 'Seleziona almeno una tipologia'}
                        />
                      )}
                    />
                  )}
                />
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/users')}
                  disabled={saveMutation.isPending}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={saveMutation.isPending}
                >
                  {isEdit ? 'Aggiorna' : 'Crea'} Utente
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Box>
    </AppLayout>
  );
};

export default UserFormPage;
